/*
  ClientPainTrendCard — aggregate pain-score view for the Client
  Detail overview tab. Pulls all of the client's pain-tracked
  programs from `/api/health-record/{email}/pain-trend` and shows:

    * A single Recharts line chart with Pre (rose) + Post (emerald)
      series across ALL programs so the reader can see whether pain
      is trending down over the whole care journey.
    * Summary stats — sessions, avg pre/post/Δ across everything,
      plus recent (last 5) so newer improvements aren't hidden by
      old sessions.
    * A per-program chip list showing session counts so the reader
      knows which programs contributed.

  Hidden entirely (returns null) when the client has no
  pain-tracked programs — keeps the Overview uncluttered for
  clients where this doesn't apply.
*/
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, Loader2 } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from "recharts";

function fmt(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(1);
}
function fmtDelta(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return (n > 0 ? "+" : "") + n.toFixed(1);
}

export default function ClientPainTrendCard({ email }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!email) return;
    setLoading(true);
    api.get(`/health-record/${encodeURIComponent(email)}/pain-trend`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [email]);

  // Chart series — chronological, MM-DD label so the axis fits.
  const chartRows = useMemo(() => {
    return (data?.entries || []).map((x) => ({
      date: (x.date || "").slice(5),
      Pre: x.pre,
      Post: x.post,
    }));
  }, [data]);

  if (loading) {
    return (
      <Card className="p-4 flex items-center gap-2 text-sm text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading pain trend…
      </Card>
    );
  }
  // Hide entirely for clients with no pain-tracked programs.
  if (!data || (data.summary?.tracked_program_count ?? 0) === 0) {
    return null;
  }

  const s = data.summary || {};
  const r = data.recent || {};
  const dTone = (v) => (v === null || v === undefined ? "text-zinc-500"
                        : Number(v) < 0 ? "text-emerald-700"
                        : Number(v) > 0 ? "text-rose-700" : "text-zinc-500");

  return (
    <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-3"
          data-testid="client-pain-trend-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <div className="p-1.5 rounded-md bg-rose-50 border border-rose-200 text-rose-800 mt-0.5">
            <Activity className="h-3.5 w-3.5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-600 font-semibold">
              Pain trend across care journey
            </div>
            <div className="text-2xs text-zinc-500">
              Aggregated from every program with pain tracking enabled.
            </div>
          </div>
        </div>
        {(data.programs || []).length > 0 && (
          <div className="flex flex-wrap gap-1 justify-end max-w-[55%]">
            {data.programs.map((p) => (
              <Badge key={p.id}
                     className="bg-zinc-100 text-zinc-800 border-zinc-200 border text-2xs font-normal"
                     data-testid={`client-pain-trend-prog-${p.id}`}
                     title={`${p.session_count} session${p.session_count !== 1 ? "s" : ""}`}>
                {p.title} · {p.session_count}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <Stat label="Sessions" value={s.session_count ?? 0} testid="cpt-sessions" />
        <Stat label="Avg pre" value={fmt(s.avg_pre)} tone="text-rose-700" testid="cpt-avg-pre" />
        <Stat label="Avg post" value={fmt(s.avg_post)} tone="text-emerald-700" testid="cpt-avg-post" />
        <Stat label="Avg change" value={fmtDelta(s.avg_delta)} tone={dTone(s.avg_delta)} testid="cpt-avg-delta" />
      </div>
      {r.n >= 2 && (
        <div className="text-2xs text-zinc-600 font-mono-num" data-testid="cpt-recent">
          Last {r.n}: pre {fmt(r.avg_pre)} · post {fmt(r.avg_post)}
          {" · Δ "}<span className={dTone(r.avg_delta)}>{fmtDelta(r.avg_delta)}</span>
        </div>
      )}

      {chartRows.length > 0 ? (
        <div className="h-48 w-full" data-testid="client-pain-trend-chart">
          <ResponsiveContainer>
            <LineChart data={chartRows} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="Pre" stroke="#e11d48" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Post" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded p-3">
          {data.programs?.length
            ? "Tracking is enabled but no sessions logged yet. Log a session from the program editor to start charting."
            : "No pain-tracked programs yet."}
        </div>
      )}
    </Card>
  );
}


function Stat({ label, value, tone, testid }) {
  return (
    <div className="rounded border border-zinc-200 px-2 py-1.5 bg-white" data-testid={testid}>
      <div className="text-2xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-sm font-semibold font-mono-num ${tone || "text-zinc-950"}`}>{value}</div>
    </div>
  );
}
